import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Button, CheckIcon, PageHeading } from '@quickmd-llc/design-system';
import { useApplicationStore } from '@data';
import { Interstitial } from '@quickmd-llc/design-system';
import CreditCardPicker from '@components/CreditCardPicker';
import { CreditCardForm } from '@components/forms/CreditCardForm/CreditCardForm';
import {
  CreditCard,
  emptyCardErrors,
} from '@components/forms/CreditCardForm/utils';
import { PatientCreditCard } from '@qmd-types/patient';
import { RefillOrderSummary } from '../RefillOrderSummary';
import { RefillDeliveryAddress } from '../RefillDeliveryAddress';
import { Cost } from './Cost';
import { createRefill } from '@api';
import { useRefillCheckup } from '../../hooks/useRefillCheckup';
import { addCard } from '@components/CreditCardPicker/AddCardPopUp/utils';
import { useLocalAdyen } from '@components/forms/CreditCardForm/hooks/useLocalAdyen';

type FormValues = {
  address: {
    streetAddress1: string;
    streetAddress2?: string;
    city: string;
    stateCode: string;
    zip: string;
    countryCode: string;
  };
  creditCard?: CreditCard;
  sameAsHomeAddress?: boolean;
};

export function PaymentSlide() {
  const patientProfile = useApplicationStore(
    ({ patient }) => patient.patientProfile,
  );
  const creditCards = useApplicationStore(({ patient }) => patient.creditCards);
  const formMethods = useForm<FormValues>({
    defaultValues: {
      address: {
        streetAddress1: '',
        streetAddress2: '',
        city: '',
        stateCode: '',
        zip: '',
        countryCode: '',
      },
      creditCard: { cardNumber: '', cardExpiration: '', cardCode: '' },
      sameAsHomeAddress: true,
    },
  });
  const { nextSlide, setCreditCard } = useRefillCheckup();
  const [isLoading, setIsLoading] = useState(false);
  const [showInterstitial, setShowInterstitial] = useState(false);
  const [cardErrors, setCardErrors] = useState(emptyCardErrors);
  const [selectedCreditCard, setSelectedCreditCard] = useState<
    PatientCreditCard | undefined
  >(undefined);

  const { handleSubmit, unregister } = formMethods;
  const showCreditCardPicker = creditCards?.length && selectedCreditCard;

  useEffect(() => {
    if (creditCards?.length) {
      const selectedCard =
        creditCards.find((card) => card.isDefault) || creditCards[0];
      if (selectedCard) {
        setSelectedCreditCard(selectedCard);
        // Sometimes the credit card form is not unregistered. Will delay
        setTimeout(() => {
          unregister('creditCard');
        }, 500);
      }
    }
  }, [creditCards, unregister]);
  const { adyen } = useLocalAdyen();

  async function handleOnSubmit(data: FormValues) {
    try {
      if (!patientProfile?.address) {
        return;
      }

      const adyenEncryptedMetadata = selectedCreditCard
        ? null
        : await adyen?.getAdyenEncryptedData();

      setIsLoading(true);
      setCardErrors(emptyCardErrors);

      let creditCardId;

      try {
        if (selectedCreditCard) {
          creditCardId = selectedCreditCard.id;
          setCreditCard(selectedCreditCard);
        } else if (data.creditCard) {
          if (!adyenEncryptedMetadata) {
            setIsLoading(false);
            return;
          }
          creditCardId = await addCard({
            data: {
              creditCard: data.creditCard,
              address: data.address,
              sameAsHomeAddress: data.sameAsHomeAddress!,
            },
            setCard: setCreditCard,
            setCardErrors,
            adyenEncryptedMetadata,
          });
          if (!creditCardId) return;
        }

        await createRefill(
          {
            address: patientProfile?.address,
            creditCardId: creditCardId || '',
          },
          Boolean(adyen),
        );

        setShowInterstitial(true);
      } catch (error: unknown) {
        console.log(error);
        setIsLoading(false);
      }
    } finally {
      adyen?.unlockFormFieldsOnPaymentError();
    }
  }

  function handleInterstitialFadeOut() {
    nextSlide();
  }

  return (
    <FormProvider {...formMethods}>
      {showInterstitial && (
        <Interstitial
          onFadeOut={handleInterstitialFadeOut}
          text="Purchase confirmed!"
        />
      )}

      <form
        className="flex flex-1 flex-col"
        onSubmit={handleSubmit(handleOnSubmit)}
      >
        <PageHeading className="mb-4">Confirm your purchase</PageHeading>

        <div className="mb-4">
          <RefillOrderSummary />
        </div>

        <div className="mb-4">
          <Cost />
        </div>

        <div className="mb-4">
          {showCreditCardPicker ? (
            <CreditCardPicker
              cardErrors={cardErrors}
              creditCards={creditCards}
              selectedCreditCard={selectedCreditCard}
              setSelectedCreditCard={setSelectedCreditCard}
            />
          ) : (
            <CreditCardForm
              cardErrors={cardErrors}
              AdyenCardForm={adyen?.AdyenCardForm}
            />
          )}
        </div>

        <div className="mb-4">
          <RefillDeliveryAddress />
        </div>

        <Button className="mt-auto w-full" loading={isLoading} type="submit">
          Confirm Purchase
          <CheckIcon iconSize="l" />
        </Button>
      </form>
    </FormProvider>
  );
}
